import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { Car } from "./CarService";
import { ExpenseSchedule, ExpenseScheduleInput } from "./ExpenseScheduleService";

// =============================================================================
// GraphQL Fragments
// =============================================================================

const VEHICLE_FINANCING_FIELDS = `
  id
  accountId
  carId
  userId
  expenseScheduleId

  financingType
  lenderName
  agreementNumber
  startDate
  endDate
  termMonths
  totalAmount
  financingCurrency
  interestRate
  downPayment

  residualValue
  mileageAllowance
  mileageAllowanceUnit
  mileageOverageCost

  notes

  status
  version
  createdBy
  updatedBy
  removedBy
  createdAt
  updatedAt
  removedAt

  paymentsRemaining
  remainingBalance
  percentComplete
  totalMileageAllowance

  estimatedMonthlyPayment
  costPrincipal
  costTotalInterest
  costTotal
  costInterestPercent
  costDownPaymentBarPercent
  costPrincipalBarPercent
  costInterestBarPercent

  mileageDriven
  mileageUsedPercent
  annualMileageRate
  projectedTotalMileage
  projectedOverage
  projectedOverageCost
  mileagePaceStatus
  mileageDataPoints

  car {
    id
    label
    mileageIn
  }
  user {
    id
    firstName
    lastName
    email
  }
  userCreated {
    id
    firstName
    lastName
  }
  userUpdated {
    id
    firstName
    lastName
  }
  expenseSchedule {
    id
    scheduleType
    scheduleDays
    totalPrice
    costWork
    costParts
    tax
    fees
    subtotal
    paidInCurrency
    whereDone
    shortNote
    comments
    nextScheduledAt
    lastAddedAt
    status
  }
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const VEHICLE_FINANCING_LIST_QUERY = gql`
  query VehicleFinancingList($filter: VehicleFinancingFilter, $params: PaginationAndSorting) {
    vehicleFinancingList(filter: $filter, params: $params) {
      code
      data {
        ${VEHICLE_FINANCING_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const VEHICLE_FINANCING_GET_QUERY = gql`
  query VehicleFinancingGet($id: ID) {
    vehicleFinancingGet(id: $id) {
      code
      data {
        ${VEHICLE_FINANCING_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const VEHICLE_FINANCING_GET_MANY_QUERY = gql`
  query VehicleFinancingGetMany($ids: [ID]) {
    vehicleFinancingGetMany(ids: $ids) {
      code
      data {
        ${VEHICLE_FINANCING_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const VEHICLE_FINANCING_CREATE_MUTATION = gql`
  mutation VehicleFinancingCreate($params: VehicleFinancingInput) {
    vehicleFinancingCreate(params: $params) {
      code
      data {
        ${VEHICLE_FINANCING_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const VEHICLE_FINANCING_UPDATE_MUTATION = gql`
  mutation VehicleFinancingUpdate($where: VehicleFinancingWhereInput, $params: VehicleFinancingInput) {
    vehicleFinancingUpdate(where: $where, params: $params) {
      code
      data {
        ${VEHICLE_FINANCING_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const VEHICLE_FINANCING_REMOVE_MUTATION = gql`
  mutation VehicleFinancingRemove($where: VehicleFinancingWhereInput) {
    vehicleFinancingRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const VEHICLE_FINANCING_REMOVE_MANY_MUTATION = gql`
  mutation VehicleFinancingRemoveMany($where: [VehicleFinancingWhereInput]) {
    vehicleFinancingRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

/**
 * Financing type constants
 */
export const FINANCING_TYPE = {
  LOAN: 'loan',
  LEASE: 'lease',
} as const;

export type FinancingTypeValue = (typeof FINANCING_TYPE)[keyof typeof FINANCING_TYPE];

/**
 * Vehicle financing status constants
 */
export const VEHICLE_FINANCING_STATUS = {
  ACTIVE: 100,
  COMPLETED: 200,
  REMOVED: 10000,
} as const;

export type VehicleFinancingStatusValue = (typeof VEHICLE_FINANCING_STATUS)[keyof typeof VEHICLE_FINANCING_STATUS];

export interface VehicleFinancing {
  id?: string | undefined;
  accountId?: string | undefined;
  carId?: string | undefined;
  userId?: string | undefined;
  /** Reference to the auto-created expense schedule for recurring payments */
  expenseScheduleId?: string | undefined;

  // Financing terms
  /** Financing type: loan, lease */
  financingType?: FinancingTypeValue | undefined;
  /** Bank, credit union, or leasing company name */
  lenderName?: string | undefined;
  /** Loan or lease agreement/account number from the lender */
  agreementNumber?: string | undefined;
  /** Loan or lease start date (ISO 8601) */
  startDate?: string | undefined;
  /** Loan maturity date or lease end date (ISO 8601). Auto-calculated from startDate + termMonths if not explicitly provided. */
  endDate?: string | undefined;
  /** Total term length in months */
  termMonths?: number | undefined;
  /** Loan principal or total lease cost */
  totalAmount?: number | undefined;
  /** Currency of financing amounts (ISO 4217) */
  financingCurrency?: string | undefined;
  /** Annual interest rate as percentage (e.g. 5.25) */
  interestRate?: number | undefined;
  /** Initial down payment amount */
  downPayment?: number | undefined;

  // Lease-specific fields
  /** Lease residual / buyout price at end of term */
  residualValue?: number | undefined;
  /** Annual mileage allowance for leases */
  mileageAllowance?: number | undefined;
  /** Unit for mileage allowance: km, mi */
  mileageAllowanceUnit?: string | undefined;
  /** Cost per km/mile over the annual mileage allowance */
  mileageOverageCost?: number | undefined;

  /** Additional terms, conditions, or notes */
  notes?: string | undefined;

  // Status and audit fields
  /**
   * Record status:
   * - 100: Active (current financing)
   * - 200: Completed (paid off / lease ended)
   * - 10000: Removed
   */
  status?: VehicleFinancingStatusValue | undefined;
  version?: number | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  removedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  removedAt?: string | undefined;

  // Computed fields
  /** Number of payments remaining based on term and elapsed months */
  paymentsRemaining?: number | undefined;
  /** Estimated remaining balance for loans */
  remainingBalance?: number | undefined;
  /** Percentage of term completed (0-100) */
  percentComplete?: number | undefined;
  /** Total mileage allowance for the full lease term */
  totalMileageAllowance?: number | undefined;

  // Computed: estimated monthly payment
  /** Estimated monthly payment calculated from financing terms (loan amortization or lease formula). Formula-based only — does not reflect the linked expense schedule payment. */
  estimatedMonthlyPayment?: number | undefined;

  // Computed: cost breakdown (for visualization)
  /** For loans: principal (totalAmount minus downPayment). For leases: total depreciation (netCapCost minus residualValue). */
  costPrincipal?: number | undefined;
  /** For loans: total interest over life of financing. For leases: total finance charges (money factor portion). */
  costTotalInterest?: number | undefined;
  /** Total all-in cost including down payment and all payments */
  costTotal?: number | undefined;
  /** Interest/finance charges as percentage of principal/depreciation (e.g. 15.2 for 15.2%) */
  costInterestPercent?: number | undefined;
  /** Down payment as percentage of total cost (for bar chart visualization, 0-100) */
  costDownPaymentBarPercent?: number | undefined;
  /** Principal/depreciation as percentage of total cost (for bar chart visualization, 0-100) */
  costPrincipalBarPercent?: number | undefined;
  /** Interest/finance charges as percentage of total cost (for bar chart visualization, 0-100) */
  costInterestBarPercent?: number | undefined;

  // Computed: mileage tracking (lease only, derived from odometer data)
  /** Actual distance driven since lease start, in mileageAllowanceUnit */
  mileageDriven?: number | undefined;
  /** Mileage driven as percentage of total allowance (0-100) */
  mileageUsedPercent?: number | undefined;
  /** Projected annual mileage rate based on current driving pattern */
  annualMileageRate?: number | undefined;
  /** Projected total mileage at end of lease term */
  projectedTotalMileage?: number | undefined;
  /** Projected overage beyond total allowance (0 if under pace) */
  projectedOverage?: number | undefined;
  /** Projected overage cost based on mileageOverageCost rate */
  projectedOverageCost?: number | undefined;
  /** Mileage pace status: under_pace, on_pace, over_pace */
  mileagePaceStatus?: string | undefined;
  /** Number of odometer data points available during lease period */
  mileageDataPoints?: number | undefined;

  // Resolved references
  car?: Car | undefined;
  user?: User | undefined;
  userCreated?: User | undefined;
  userUpdated?: User | undefined;
  /** Linked expense schedule for recurring payments */
  expenseSchedule?: ExpenseSchedule | undefined;
}

export interface VehicleFinancingInput {
  id?: string | null | undefined;
  carId?: string | null | undefined;
  /** Financing type: loan, lease */
  financingType?: FinancingTypeValue | null | undefined;
  /** Bank, credit union, or leasing company name */
  lenderName?: string | null | undefined;
  /** Loan or lease agreement/account number */
  agreementNumber?: string | null | undefined;
  /** Loan or lease start date (ISO 8601) */
  startDate?: string | null | undefined;
  /**
   * Loan maturity date or lease end date (ISO 8601).
   * Optional — if omitted but startDate and termMonths are provided,
   * endDate is auto-calculated as startDate + termMonths on the server.
   * If explicitly provided, it takes precedence over the calculated value.
   */
  endDate?: string | null | undefined;
  /** Total term length in months */
  termMonths?: number | null | undefined;
  /** Loan principal or total lease cost */
  totalAmount?: number | null | undefined;
  /** Currency of financing amounts (ISO 4217) */
  financingCurrency?: string | null | undefined;
  /** Annual interest rate as percentage (e.g. 5.25) */
  interestRate?: number | null | undefined;
  /** Initial down payment amount */
  downPayment?: number | null | undefined;
  /** Lease residual / buyout price */
  residualValue?: number | null | undefined;
  /** Annual mileage allowance for leases */
  mileageAllowance?: number | null | undefined;
  /** Unit for mileage allowance: km, mi */
  mileageAllowanceUnit?: string | null | undefined;
  /** Cost per km/mile over allowance */
  mileageOverageCost?: number | null | undefined;
  /** Additional terms or notes */
  notes?: string | null | undefined;
  /** Record status: 100=Active, 200=Completed */
  status?: VehicleFinancingStatusValue | null | undefined;

  /**
   * Payment schedule configuration using the standard ExpenseScheduleInput.
   * When provided, an ExpenseSchedule record is auto-created (on create) or
   * updated (on update) to handle recurring payments.
   * Supports weekly, monthly, yearly, and one-time payment frequencies.
   * Fields like carId and kindId are auto-populated from the financing context.
   * If null/omitted on update, the existing schedule is left unchanged.
   */
  expenseSchedule?: ExpenseScheduleInput | null | undefined;
}

export interface VehicleFinancingFilter {
  id?: string[] | null | undefined;
  accountId?: string[] | null | undefined;
  userId?: string[] | null | undefined;
  /** Filter by vehicle ID(s) */
  carId?: string[] | null | undefined;
  /** Filter by financing type(s): loan, lease */
  financingType?: FinancingTypeValue[] | null | undefined;
  /** Filter by linked expense schedule ID(s) */
  expenseScheduleId?: string[] | null | undefined;
  /** Filter by status(es): 100=Active, 200=Completed, 10000=Removed */
  status?: VehicleFinancingStatusValue[] | null | undefined;
  /** Search by lender name */
  searchKeyword?: string | null | undefined;
}

export interface VehicleFinancingWhereInput {
  id?: string | undefined;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get human-readable label for financing type
 */
export function getFinancingTypeLabel(financingType: FinancingTypeValue): string {
  const labels: Record<FinancingTypeValue, string> = {
    [FINANCING_TYPE.LOAN]: 'Loan',
    [FINANCING_TYPE.LEASE]: 'Lease',
  };
  return labels[financingType] || financingType;
}

/**
 * Get human-readable label for financing status
 */
export function getFinancingStatusLabel(status: VehicleFinancingStatusValue): string {
  const labels: Record<VehicleFinancingStatusValue, string> = {
    [VEHICLE_FINANCING_STATUS.ACTIVE]: 'Active',
    [VEHICLE_FINANCING_STATUS.COMPLETED]: 'Completed',
    [VEHICLE_FINANCING_STATUS.REMOVED]: 'Removed',
  };
  return labels[status] || String(status);
}

/**
 * Format currency amount for display
 */
export function formatFinancingAmount(
  amount: number | undefined | null,
  currency?: string | null
): string {
  if (amount == null) return '—';

  try {
    return new Intl.NumberFormat('en-US', {
      style: currency ? 'currency' : 'decimal',
      currency: currency || undefined,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency || ''}`.trim();
  }
}

/**
 * Format interest rate for display (e.g. "5.25%")
 */
export function formatInterestRate(rate: number | undefined | null): string {
  if (rate == null) return '—';
  return `${rate.toFixed(2)}%`;
}

/**
 * Calculate monthly payment using standard amortization formula.
 * Useful for pre-filling the monthly payment field when user enters loan terms.
 *
 * @param principal - Loan principal (totalAmount - downPayment)
 * @param annualRate - Annual interest rate as percentage (e.g. 5.25)
 * @param termMonths - Total term in months
 * @returns Monthly payment amount
 */
export function calculateMonthlyPayment(
  principal: number,
  annualRate: number,
  termMonths: number
): number {
  if (principal <= 0 || termMonths <= 0) return 0;

  // Zero interest — simple division
  if (annualRate === 0) {
    return principal / termMonths;
  }

  const monthlyRate = annualRate / 100 / 12;
  const compound = Math.pow(1 + monthlyRate, termMonths);
  const payment = principal * (monthlyRate * compound) / (compound - 1);

  return Math.round(payment * 100) / 100;
}

// =============================================================================
// Service Class
// =============================================================================

/**
 * Vehicle financing service that handles all financing-related API calls.
 * Supports CRUD operations for loan and lease records.
 */
export class VehicleFinancingService {
  /**
   * Get a list of vehicle financing records with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: VehicleFinancingFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(VEHICLE_FINANCING_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single vehicle financing record by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(VEHICLE_FINANCING_GET_QUERY, {
      id,
    });
    return result;
  }

  /**
   * Get multiple vehicle financing records by IDs
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(VEHICLE_FINANCING_GET_MANY_QUERY, {
      ids,
    });
    return result;
  }

  /**
   * Create a new vehicle financing record.
   * If expenseSchedule is provided, auto-creates a linked expense schedule for recurring payments.
   */
  static async create(params: VehicleFinancingInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(VEHICLE_FINANCING_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing vehicle financing record.
   * If expenseSchedule is provided, updates the linked expense schedule.
   */
  static async update(
    where: VehicleFinancingWhereInput,
    params: VehicleFinancingInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(VEHICLE_FINANCING_UPDATE_MUTATION, {
      where,
      params,
    });
    return result;
  }

  /**
   * Remove/delete a vehicle financing record (soft delete).
   * Also disables the linked expense schedule.
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(VEHICLE_FINANCING_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }

  /**
   * Remove/delete multiple vehicle financing records (soft delete)
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(VEHICLE_FINANCING_REMOVE_MANY_MUTATION, {
      where,
    });
    return result;
  }
}